import React, { useState, useEffect, useRef } from 'react';

import { ExclamationCircleIcon } from '@heroicons/react/solid';
import clsx from 'clsx';
import type { DownshiftState, StateChangeOptions } from 'downshift';
import Downshift from 'downshift';

import type {
  UIComponentBase,
  UIComponentSizes,
  UIInputValue,
  UIOnChangeFn,
  UISelectionType,
} from '../../@types/types';
import Icon from './Icon';
import type { InputFieldProps } from './InputField';
import InputField from './InputField';
import InputReadOnly from './InputReadOnly';

type InputSelectStyle = 'button' | 'input' | 'menuInner' | 'menuItem' | 'menuOuter' | 'menuItemDisabled';
type InputSelectStyles = Record<InputSelectStyle, string>;

export const sizes: UIComponentSizes = {
  normal: 'min-h-11',
  small: 'min-h-8',
};

export const styles: InputSelectStyles = {
  button:
    'bg-white border border-gray-300 px-3 py-2 rounded text-gray-900 h-10 flex justify-between items-center w-full focus:outline-none focus:shadow-outline',
  input: 'border-b border-gray-100 px-3 py-2 text-gray-900 w-full focus:outline-none',
  menuInner: 'overflow-y-auto max-h-96',
  menuItem: 'py-2 px-3 cursor-pointer text-sm font-normal',
  menuOuter:
    'border-gray-300 border rounded absolute inset-x-0 top-0 z-20 bg-white text-gray-900 mt-11  shadow overflow-hidden',
  menuItemDisabled: 'cursor-not-allowed bg-gray-50 text-gray-300',
};

export interface InputSelectProps extends InputFieldProps {
  isReadOnly?: boolean;
  isSearchable?: boolean;
  labelText?: string;
  name?: string;
  options?: UISelectionType[];
  value?: UIInputValue;
  initialSelectedItem?: UIInputValue;
  placeholder?: string;
  'aria-labelledby'?: string;
  formatter?: (value: any) => string | number | false;
  itemFormatter?: (value: any) => string;
  onChange?: UIOnChangeFn;
  onSelect?: UIOnChangeFn;
  buttonClassName?: string;
  valueClassName?: string;
}

interface MenuContainerProps {
  anchorRef?: React.RefObject<HTMLInputElement>;
  children?: UIComponentBase['children'];
}

type UseDownshiftStyleState = React.CSSProperties | undefined;
type StateReducerFn =
  | ((
      state: DownshiftState<UISelectionType>,
      changes: StateChangeOptions<UISelectionType>
    ) => Partial<StateChangeOptions<UISelectionType>>)
  | undefined;

const useDownshiftStyle = (
  anchorRef?: React.RefObject<HTMLInputElement>,
  ref?: React.RefObject<HTMLInputElement>
): UseDownshiftStyleState => {
  const [style, setStyle] = useState<UseDownshiftStyleState>(undefined);

  useEffect(() => {
    const element = ref?.current;
    const anchorElement = anchorRef?.current;

    if (element && anchorElement) {
      const anchor = anchorElement.getBoundingClientRect();
      const bound = element.getBoundingClientRect();

      // if overflowing
      if (anchor.y + anchor.height > window.innerHeight - 64) {
        const height = bound.bottom - bound.y + 8; // get element height
        const style = { marginTop: -height };
        setStyle({ ...style });
      }
    }
  }, [ref]);

  return style;
};

const stateReducer: StateReducerFn = (state, changes) => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
      return { ...changes, inputValue: '' };

    case Downshift.stateChangeTypes.clickButton:
    case Downshift.stateChangeTypes.changeInput:
      return { ...changes, highlightedIndex: 0 };

    default:
      return changes;
  }
};

export const MenuContainer: React.FC<MenuContainerProps> = ({ anchorRef, children }) => {
  const ref = useRef(null);
  const style = useDownshiftStyle(anchorRef, ref);

  return (
    <div className={styles.menuOuter} ref={ref} style={style}>
      {children}
    </div>
  );
};

const InputSelect: React.FC<InputSelectProps> = ({
  className,
  buttonClassName,
  valueClassName,
  descriptionText,
  errors,
  isReadOnly,
  isSearchable = true,
  labelText,
  name = 'input',
  options,
  placeholder = 'Select',
  size = 'normal',
  value,
  id: _id,
  formatter,
  itemFormatter,
  onChange,
  onSelect: _onSelect,
  ...props
}) => {
  const getItemToString = (item: UISelectionType | null): string => {
    return (item?.value ?? '').toString();
  };

  return (
    <InputField
      className={className}
      descriptionText={descriptionText}
      labelText={labelText}
      name={name}
      errors={errors}
      {...props}
    >
      {isReadOnly && (
        <InputReadOnly
          {...props}
          aria-describedby={descriptionText ? `${name}Description` : undefined}
          name={name}
          value={value || ''}
        />
      )}

      {!isReadOnly && (
        <Downshift
          stateReducer={stateReducer}
          // @ts-ignore
          selectedItem={value ?? ''}
          itemToString={getItemToString}
          onSelect={(item: any) => {
            if (onChange) onChange(item?.value, name);
          }}
        >
          {({
            getItemProps,
            getMenuProps,
            getToggleButtonProps,
            highlightedIndex,
            isOpen,
            inputValue,
            getInputProps,
          }) => {
            const normalized = String(inputValue ?? '').toLowerCase();
            const byInputValue = (item: UISelectionType) =>
              String(item.name ?? '')
                .toLowerCase()
                .includes(normalized);
            const filteredItems = options?.filter(byInputValue);
            const selectableItems = isSearchable ? filteredItems : options;
            const hasSelection = Boolean(value);

            return (
              <div className="relative flex">
                <button
                  {...getToggleButtonProps()}
                  aria-label={labelText}
                  aria-labelledby={props['aria-labelledby']}
                  type="button"
                  className={clsx(
                    'aui-input aui-input-select flex text-sm border border-gray-300 rounded-md m-auto py-2.5 px-3 flex-1 shadow-sm justify-between ',
                    errors && 'aui-input--errors border-red-300',
                    sizes[size],
                    buttonClassName
                  )}
                >
                  {!hasSelection && <span className="text-gray-700">{placeholder}</span>}

                  {hasSelection && formatter && <span className={valueClassName}>{formatter(value)}</span>}

                  {hasSelection && !formatter && <span className={valueClassName}>{value}</span>}

                  {!isOpen && <Icon name="chevron-down" className="ml-2 fill-current text-gray-800 self-center" />}

                  {isOpen && <Icon name="chevron-up" className="ml-2 fill-current text-blue-700 self-center" />}
                </button>

                {isOpen && (
                  <MenuContainer>
                    <div {...getMenuProps()}>
                      {isSearchable && (
                        <input autoFocus className={styles.input} placeholder="Search..." {...getInputProps()} />
                      )}

                      <ul className={styles.menuInner}>
                        {(selectableItems || []).map((item, index) => {
                          const { isDisabled = false } = item;
                          const result =
                            (itemFormatter && itemFormatter(item.value)) ||
                            (formatter && formatter(item.value)) ||
                            item.name;

                          const { onClick, onMouseDown, onMouseMove, ...itemProps } = getItemProps({ item });

                          const actionProps = isDisabled
                            ? {}
                            : {
                                onClick,
                                onMouseDown,
                                onMouseMove,
                              };

                          return (
                            <li
                              className={clsx(
                                styles.menuItem,
                                index === highlightedIndex && !isDisabled && 'bg-gray-100',
                                isDisabled && styles.menuItemDisabled
                              )}
                              index={index}
                              key={JSON.stringify(item.value)}
                              {...actionProps}
                              {...itemProps}
                            >
                              {result}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </MenuContainer>
                )}
                {errors && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div>
                )}
              </div>
            );
          }}
        </Downshift>
      )}
    </InputField>
  );
};

export default InputSelect;
